import { Button, Checkbox, Dialog, DialogContent, FormControlLabel, Grid, Typography, useTheme } from "@mui/material";
import isEqual from "lodash/isEqual";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { LegalEntitiesFilter } from "src/models/tna/tna-day-planning-schedule/legal-entities-filter";
import { TnADayPlanningSchedulesApiService } from "src/services/tna-day-planning-schedules/tna-day-planning-schedules-api.service";
import { intlFormatDateFullMonth } from "src/utils/time-utils";
import { toastifyResponse } from "src/utils/toastify-response/toastify-response";
import { v4 as uuidv4 } from "uuid";
import ConfirmationChangesDialog from "../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { CompanyAccountRef } from "../../../models/company-accounts/company-accounts";
import { CompanyLegalEntityRef } from "../../../models/company-accounts/company-legal-entities";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { EmployeeRef } from "../../../models/employees";
import { TnADayDataRef } from "../../../models/tna/tna-data/tna-day-data-ref";
import { TnASchedulePerDayPlanningSTO } from "../../../models/tna/tna-schedule-per-day-planning/tna-schedule-per-day-planning-sto";
import { TnAScheduleRef } from "../../../models/tna/tna-schedules/tna-schedule-ref";
import { TnACalendarDaysDTO } from "../../../models/tna-calendar-days/tna-calendar-days-dto";
import AddEditButton from "../../../reusable-components/AddEditButton/AddEditButton";
import AutocompleteDropDown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { TnASchedulePerDayPlanningApiService } from "../../../services/tna-schedule-per-day-planning/tna-schedule-per-day-planning-api.service";

interface Props {
  schedule?: TnAScheduleRef | null;
  companyAccount?: CompanyAccountRef | null;
  legalEntity?: CompanyLegalEntityRef | null;
  employee?: EmployeeRef | null;
  tnADate?: CustomDate | null;
  handleNotifyParent: () => void;
  children?: React.ReactNode;
  buttonText?: string;
  buttonTitle?: string;
  schedulePlanningId?: string | null;
  disabled?: boolean;
  row?: TnACalendarDaysDTO | null;
}

interface State {
  isDialogOpen: boolean;
  loading: boolean;
  schedulePlanning: TnASchedulePerDayPlanningSTO;
  isCCDOpen: boolean;
  areFieldsValidated: boolean;
  applyToFollowingDays: boolean;
  error: string;
}

const AddEditSchedulePlanning = ({
  schedule,
  companyAccount,
  legalEntity,
  employee,
  tnADate,
  handleNotifyParent,
  children,
  buttonText,
  buttonTitle,
  schedulePlanningId,
  disabled = false,
  row = null,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_schedule_planning";
  const theme = useTheme();

  const DEFAULT_ITEM: TnASchedulePerDayPlanningSTO = useMemo(() => {
    return {
      id: "",
      companyAccount: companyAccount ?? null,
      legalEntity: legalEntity ?? null,
      employee: employee ?? null,
      tnADate: tnADate ?? null,
      schedule: schedule ?? null,
    } as TnASchedulePerDayPlanningSTO;
  }, [companyAccount, legalEntity, employee, tnADate, schedule]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      schedulePlanning: DEFAULT_ITEM,
      isCCDOpen: false,
      areFieldsValidated: !!schedule,
      applyToFollowingDays: false,
      error: "",
    };
  }, [DEFAULT_ITEM, schedule]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const setOpenDialog = useCallback((value: boolean) => setState((state) => ({ ...state, isDialogOpen: value })), []);

  const handleChangeField = useCallback((field: keyof TnASchedulePerDayPlanningSTO, value: any) => {
    let newValue = value;
    // Extract the schedule from TnADayPlanningScheduleDTO when user selects from dropdown
    if (field === "schedule" && value && typeof value === "object" && value.schedule) {
      newValue = value.schedule as TnAScheduleRef;
    }
    setState((state) => ({
      ...state,
      schedulePlanning: {
        ...state.schedulePlanning,
        [field]: newValue,
      },
    }));
  }, []);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.schedulePlanning, INITIAL_STATE.schedulePlanning)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const areFieldsValidated = useCallback((): boolean => {
    return !!state.schedulePlanning.employee && !!state.schedulePlanning.tnADate && !!state.schedulePlanning.schedule;
  }, [state.schedulePlanning]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.schedulePlanning, areFieldsValidated]);

  const handleSave = useCallback(
    async (isDeleted?: boolean) => {
      try {
        setState((state) => ({ ...state, loading: true, error: "" }));

        const itemsToSave: TnASchedulePerDayPlanningSTO[] = [];

        if (isDeleted) {
          itemsToSave.push({
            legalEntity,
            isDeleted: true,
            id: schedulePlanningId,
            employee,
            schedule,
            tnADate,
          } as TnASchedulePerDayPlanningSTO);
        } else if (state.applyToFollowingDays && tnADate && row) {
          const currentDate = CustomDate.ToDate(tnADate);
          if (currentDate) {
            const currentDayOfWeek = currentDate.getDay();
            const daysUntilSunday = currentDayOfWeek === 0 ? 0 : 7 - currentDayOfWeek;

            for (let i = 0; i <= daysUntilSunday; i++) {
              const targetDate = new Date(currentDate);
              targetDate.setDate(targetDate.getDate() + i);

              const dayOfMonth = targetDate.getDate();
              const monthOfDay = targetDate.getMonth();
              const yearOfDay = targetDate.getFullYear();

              const dayData = row?.daysData?.find((day: TnADayDataRef) => {
                const dayDate = CustomDate.ToDate(day?.tnADate);
                return (
                  dayDate &&
                  dayDate.getDate() === dayOfMonth &&
                  dayDate.getMonth() === monthOfDay &&
                  dayDate.getFullYear() === yearOfDay
                );
              });

              itemsToSave.push({
                ...state.schedulePlanning,
                id: i === 0 && schedulePlanningId ? schedulePlanningId : uuidv4(),
                tnADate: dayData?.tnADate ?? CustomDate.FromDate(targetDate),
                companyAccount: dayData?.companyAccount ?? companyAccount ?? null,
                legalEntity: dayData?.legalEntity ?? legalEntity ?? null,
                employee: dayData?.employee ?? employee ?? null,
              });
            }
          }
        } else {
          itemsToSave.push({
            ...state.schedulePlanning,
            id: schedulePlanningId ?? uuidv4(),
          });
        }

        const response = await new TnASchedulePerDayPlanningApiService().saveItems(itemsToSave);

        if (!response.error) {
          handleNotifyParent();
          setState({ ...INITIAL_STATE });
          toastifyResponse(response);
        } else {
          setState((state) => ({ ...state, loading: false, error: response.error || "Save failed" }));
        }
      } catch (error: any) {
        setState((state) => ({ ...state, loading: false, error: error.message || "An error occurred" }));
      }
    },
    [
      state.schedulePlanning,
      state.applyToFollowingDays,
      handleNotifyParent,
      INITIAL_STATE,
      schedulePlanningId,
      legalEntity,
      employee,
      schedule,
      tnADate,
      row,
      companyAccount,
    ]
  );

  return (
    <>
      <AddEditButton
        title={buttonTitle ?? (schedule ? t(`${tRP}.edit_schedule`) : t(`${tRP}.add_schedule`))}
        effect={"edit"}
        onClick={() => {
          if (!disabled) {
            setOpenDialog(true);
          }
        }}
        {...(!children && buttonText && { buttonText })}
        customStyle={{
          minWidth: children ? "fit-content" : undefined,
          height: children ? "fit-content" : undefined,
          padding: children ? 5 : undefined,
          cursor: disabled ? "" : "pointer",
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
        disabled={disabled}
      >
        {children}
      </AddEditButton>
      <Dialog
        open={state.isDialogOpen}
        onClose={(event: any, reason) => {
          if (reason === "backdropClick") {
            event.stopPropagation();
          }
          resetAndCloseDialog();
        }}
        fullWidth
        maxWidth="sm"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle
          title={
            !schedule
              ? t(`${tRP}.title_for_add_component`, { employee: employee?.name })
              : t(`${tRP}.title_for_edit_component`, { employee: employee?.name })
          }
          handleClose={resetAndCloseDialog}
        />
        <LoadingOverlay loading={state.loading}>
          <DialogContent>
            <Grid sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Typography>
                <Trans
                  i18nKey={`${tRP}.select_schedule`}
                  components={{ b: <b></b> }}
                  values={{
                    date: intlFormatDateFullMonth(
                      CustomDate.ToDate(state.schedulePlanning.tnADate as CustomDate) as Date
                    ),
                  }}
                />
              </Typography>
              <AutocompleteDropDown
                labelKey={t(`${tRP}.fields.schedule`)}
                field="schedule"
                serviceApi={new TnADayPlanningSchedulesApiService()}
                selectedValue={state.schedulePlanning.schedule}
                handleChange={handleChangeField}
                required
                propsFilter={
                  {
                    legalEntityIds: state.schedulePlanning.legalEntity ? [state.schedulePlanning.legalEntity.id] : [],
                  } as LegalEntitiesFilter
                }
                fieldProp="schedule"
                fieldPropSecondDepth="name"
                compareByField="schedule"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.applyToFollowingDays}
                    onChange={(e) => setState((state) => ({ ...state, applyToFollowingDays: e.target.checked }))}
                  />
                }
                label={
                  <Typography>
                    <Trans
                      i18nKey={`${tRP}.apply_until_end_of_week`}
                      components={{ b: <b></b> }}
                    />
                  </Typography>
                }
              />
            </Grid>
          </DialogContent>

          <DialogActions
            handleSave={handleSave}
            disabledSaveButton={!areFieldsValidated() || !!state.error}
            handleCancel={resetAndCloseDialog}
            // loading={state.loading}
            error={state.error}
          >
            {schedulePlanningId && (
              <Button
                onClick={() => handleSave(true)}
                color={"error"}
                variant="contained"
                style={{ textTransform: "none" }}
              >{`${t(`${tRP}.delete_for_this_date`)}`}</Button>
            )}
          </DialogActions>
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default React.memo(AddEditSchedulePlanning);
